import { useEffect, useMemo, useRef, useState } from 'react';
const WORDS_PER_TICK = 3; // words rendered per tick
const TICK_INTERVAL = 50; // ms between ticks

export function useTypewriter(text) {
  // Tokens alternate between words and whitespace, e.g. ["Hello", " ", "world"]
  const [wordIndex, setWordIndex] = useState(0);
  const tokensRef = useRef([]);
  const intervalRef = useRef(null);

  // Re-tokenize when text changes
  const tokens = useMemo(() => text ? text.split(/(\s+)/) : [], [text]);
  tokensRef.current = tokens;

  // When text grows (new deltas), the existing wordIndex stays valid.
  // When text is cleared (stream reset), reset the index.
  useEffect(() => {
    if (!text) {
      setWordIndex(0);
    }
  }, [text]);
  useEffect(() => {
    if (!tokens.length) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    // If we've already revealed everything, no timer needed
    if (wordIndex >= tokens.length) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    // Start ticking to reveal more words
    if (!intervalRef.current) {
      intervalRef.current = setInterval(() => {
        setWordIndex(prev => {
          const next = prev + WORDS_PER_TICK;
          if (next >= tokensRef.current.length) {
            if (intervalRef.current) {
              clearInterval(intervalRef.current);
              intervalRef.current = null;
            }
            return tokensRef.current.length;
          }
          return next;
        });
      }, TICK_INTERVAL);
    }
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [tokens.length, wordIndex]);
  const displayedText = tokens.slice(0, wordIndex).join('');
  const isComplete = tokens.length > 0 && wordIndex >= tokens.length;
  return {
    displayedText,
    isComplete
  };
}